import React from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import BillPayBankAccounts, {
  type BillPayFundingAccount,
  type BillPayKybStatus,
} from 'src/feature/BillPayBankAccounts/BillPayBankAccounts'
import { useTranslate } from 'src/localization/Trns'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { isBillPayAchEnabled } from 'src/utils/featureFlags'

/**
 * BankAccountsPage — Bill Pay Milestone 1: the org's "Bank Account" settings
 * surface (Settings → General → Bank Account, `/organizationSettings/:id/bankaccounts`).
 *
 * Renders Richa's two-card KYB-gated design (see the BillPayBankAccounts widget).
 *
 * Gating:
 *  - Feature flag `bill_pay_ach` (default off; allowlist for pilot orgs).
 *  - Owner/admin only (`hasOrgAdminPermissions`).
 *  - Tier (Pro/Enterprise): NOT wired here yet — needs a backend `/features`
 *    Chargebee entitlement; wiring `SaasUpgradeOrContent` before that exists
 *    would gate every SaaS org out. TODO(bill-pay tier gate).
 */
const BankAccountsPage: React.FC = () => {
  const translate = useTranslate()
  const currentUser = useCurrentUser()
  const appName = useSelector(selectAppName)
  const remoteConfig = useSelector(selectFeatureFlaggerFromState)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)

  const billPayAchEnabled = isBillPayAchEnabled(remoteConfig)

  if (!loadedRemoteConfig || currentUser === undefined) {
    return <LoadingBackgroundPage />
  }

  if (!billPayAchEnabled || !currentUser.hasOrgAdminPermissions) {
    return <NotFoundBackgroundPage />
  }

  // TODO(bill-pay M1): wire real state — the org's KYB/legal-entity status and
  // its funding accounts — from Donovan's payments endpoints (extend-api #9882).
  // Until then this renders the first-time "unverified → locked" state (the
  // design's default). No action handlers are passed yet, so the CTAs render
  // disabled with a "coming soon" tooltip rather than as live buttons that
  // no-op — the Start-verification (KYB modal) and Add-bank-account (details
  // modal) flows are the next increments.
  const kybStatus: BillPayKybStatus = 'unverified'
  const account: BillPayFundingAccount | null = null

  return (
    <>
      <Helmet>
        <title>{`Bank Account | ${appName}`}</title>
        <meta
          name="description"
          content={`Bank Account settings page of ${appName}`}
        />
      </Helmet>
      <LayoutPage title={<span>{translate('Bank Account')}</span>}>
        <BillPayBankAccounts kybStatus={kybStatus} account={account} />
      </LayoutPage>
    </>
  )
}

export default BankAccountsPage
